import React, {useCallback, useEffect, useRef, useState} from "react";
import {isMobile, isTablet} from "react-device-detect";
import axios from "../../../services/axiosInstance";
import Picker from "@emoji-mart/react";
import {useStoreWebSocket} from "../../../services/StoreWebsocketContext";
import {Empty, chatEnterAction} from "./StoreUI";
import {STORE_CHAT_READ_EVENT} from "./storeChatEvents";
import {TbPaperclip, TbMoodSmile, TbSend, TbDownload} from "react-icons/tb";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사 ↔ 비토리지 대화 패널 (상점사 화면 전용)
 * ════════════════════════════════════════════════════════════════════════════════
 *  ※ 관리자 [상점사] 탭이 쓰는 StoreChatBoard.js 와는 별개의 파일이다.
 *    관리자 화면은 기존 디자인을 그대로 유지해야 하므로 건드리지 않고,
 *    상점사가 보는 화면만 여명 디자인으로 새로 만들었다.
 *    (매크로 / 메세지 수정·삭제는 관리자 전용 기능이라 이 패널에는 없다)
 *
 *  [기능]
 *   - 실시간 수신 : 상점사 전용 웹소켓(StoreWebsocketContext) 구독
 *   - 첨부 : 이미지/영상은 말풍선에서 바로 보이고, 원본은 따로 내려받는다
 *   - 이모티콘 : 회원채팅과 동일한 emoji-mart 피커
 *   - 입력창은 내용에 따라 자동으로 늘어난다(최대 6줄)
 * ════════════════════════════════════════════════════════════════════════════════
 */

/*
 * 기기 판정 — 모듈 로드 시 한 번만 한다.
 *  react-device-detect 는 UA 로 정해지는 상수라 리렌더와 무관하고,
 *  App.js 가 PC/모바일 화면을 가르는 기준과 같은 값이다.
 */
const IS_TOUCH_DEVICE = isMobile || isTablet;

function StoreChatPanel({storeSeqNum, loadMessages, sendMessage, height}) {

    const {storeConnected, storeSubscribe} = useStoreWebSocket();

    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [emojiOpen, setEmojiOpen] = useState(false);

    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);

    /** 상점사가 보낸 메세지인지 (chatRole '1' = 상점사) */
    const MY_ROLE = '1';

    // ── 스크롤 하단 고정 ────────────────────────────────────────────────────
    const scrollToBottom = useCallback((smooth) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto'});
    }, []);

    /**
     * 지금 바닥 근처를 보고 있는가
     *  예전 대화를 보려고 위로 올려둔 상태에서 새 메세지가 오면
     *  무조건 바닥으로 끌어내려 읽던 위치를 잃어버린다. 바닥 80px 이내일 때만 따라간다.
     */
    const isNearBottom = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    }, []);

    // ── 조회 ───────────────────────────────────────────────────────────────
    /** @param force true 면 스크롤 위치와 무관하게 바닥으로 (최초진입 / 내가 보냈을 때) */
    const refresh = useCallback(async (smooth, force) => {
        const stick = force || isNearBottom();   // 목록을 갈아끼우기 '전에' 판단
        try {
            const list = await loadMessages();
            setMessages(Array.isArray(list) ? list.filter(m => m.removeFl !== true) : []);
            if (stick) setTimeout(() => scrollToBottom(smooth), 50);
            // 목록조회 시 서버가 읽음처리까지 끝낸다.
            // 레이아웃의 [채팅문의] 미확인 배지가 곧바로 줄어들도록 알린다.
            window.dispatchEvent(new Event(STORE_CHAT_READ_EVENT));
        } catch (e) {
            /* 조회 실패 시 기존 목록 유지 */
        }
    }, [loadMessages, scrollToBottom, isNearBottom]);

    // ── 실시간 수신 ────────────────────────────────────────────────────────
    //  storeSubscribe 는 '구독 의사' 를 등록한다. 연결 전에 불러도 되고,
    //  끊겼다 붙어도 컨텍스트가 자동으로 다시 구독한다.
    //  refresh 를 의존성에 넣으면 부모 렌더마다 재구독되므로 ref 로 최신값만 참조한다.
    const refreshRef = useRef(refresh);
    useEffect(() => {
        refreshRef.current = refresh;
    }, [refresh]);

    /*
     * 최초진입(= 대화상대가 바뀔 때)에만 최신 메세지부터 보여준다.
     *  의존성을 [refresh] 로 두면 부모가 loadMessages 를 인라인으로 넘기는 순간
     *  리렌더마다 force 스크롤이 일어나 위 내용을 읽을 수 없게 된다.
     */
    useEffect(() => {
        refreshRef.current(false, true);
    }, [storeSeqNum]);

    useEffect(() => {
        if (!storeSeqNum) return;
        return storeSubscribe(
            `store-${storeSeqNum}`,
            `/topic/storeChat/${storeSeqNum}`,
            () => refreshRef.current(true),
        );
    }, [storeSeqNum, storeSubscribe]);

    // ── 이모티콘 ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!emojiOpen) return;
        const onOutside = (e) => {
            if (e.target.closest && (e.target.closest('em-emoji-picker') || e.target.closest('[data-store-emoji-toggle]'))) return;
            setEmojiOpen(false);
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [emojiOpen]);

    // ── 입력창 자동 높이 ────────────────────────────────────────────────────
    /*
     * 높이 계산은 값이 실제로 그려진 뒤(useEffect)에 해야 한다.
     * onChange 안에서 계산하면 코드로 값을 넣었을 때 scrollHeight 가 이전 값이라 안 늘어난다.
     */
    const handleTextChange = (value) => setText(value);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`; // 최대 6줄
    }, [text]);

    const pickEmoji = (emoji) => handleTextChange(text + (emoji?.native || ''));

    // ── 전송 ───────────────────────────────────────────────────────────────
    const handleSend = async () => {
        const content = text.trim();
        if (!content || sending) return;

        setSending(true);
        try {
            await sendMessage({storeSeqNum, chatKey: content, chatRole: MY_ROLE});
            setText('');
            await refresh(true, true); // 내가 보낸 직후에는 항상 바닥으로
        } catch (e) {
            /* 실패 시 입력값 유지 */
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        // 규칙은 StoreUI 의 chatEnterAction 한 곳에 있다
        if (chatEnterAction(e, IS_TOUCH_DEVICE) !== 'send') return;
        e.preventDefault();
        handleSend();
    };

    // ── 파일첨부 ───────────────────────────────────────────────────────────
    const handleFilePick = () => fileInputRef.current?.click();

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        setUploadError('');
        if (!file || uploading) return;

        if (file.size > 100 * 1024 * 1024) {
            setUploadError('100MB 이하 파일만 보낼 수 있습니다');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post('/storeChat/uploadFile', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
            const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;

            await sendMessage({
                storeSeqNum,
                chatKey: parsed.printFilename,
                fileNm: parsed.originalFilename,
                chatRole: MY_ROLE,
            });
            await refresh(true, true); // 내가 올린 파일은 바로 보여준다
        } catch (err) {
            setUploadError('파일을 보내지 못했습니다. 잠시 후 다시 시도해주세요');
        } finally {
            setUploading(false);
        }
    };

    // ── 표시 유틸 ──────────────────────────────────────────────────────────
    const isFileMessage = (msg) => typeof msg.chatKey === 'string' && msg.chatKey.startsWith('chatFile');
    const isImageMessage = (msg) => isFileMessage(msg) && /\.(png|jpe?g|gif|webp|bmp)$/i.test(msg.chatKey);
    const isVideoMessage = (msg) => isFileMessage(msg) && /\.(mp4|mov|avi|webm|mkv)$/i.test(msg.chatKey);

    const timeText = (msg) => {
        if (!msg.regTime) return '';
        const t = msg.regTime.split(' ')[1];
        return t ? t.slice(0, 5) : '';
    };

    const downloadUrl = (msg) =>
        `${axios.defaults.baseURL}/storeChat/downloadFile?filename=${encodeURIComponent(msg.chatKey)}` +
        `&originalName=${encodeURIComponent(msg.fileNm || msg.chatKey)}`;

    /** 이미지 프리뷰 로드 실패 시 원본으로 폴백 */
    const onImgError = (e, msg) => {
        if (e.target.dataset.fallback === '1') return;
        e.target.dataset.fallback = '1';
        e.target.src = msg.fileUrl;
    };

    return (
        <div className="st-glass tw-flex tw-flex-col tw-overflow-hidden"
             style={{height: height || 'calc(100vh - 290px)', minHeight: '480px'}}>

            {/*헤더*/}
            <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2.5 tw-border-b tw-px-5 tw-py-3.5"
                 style={{borderColor: 'var(--st-glass-line)'}}>
                <span aria-hidden="true" className="tw-h-2 tw-w-2 tw-rounded-full tw-transition-colors"
                      style={{
                          background: storeConnected ? '#7FC79A' : '#C9CDD3',
                          boxShadow: storeConnected ? '0 0 0 4px rgba(159,216,180,.25)' : 'none',
                      }}/>
                <p className="st-display tw-text-[15px] tw-text-[color:var(--st-text)]">비토리지 담당자</p>
                <span className="tw-text-[11.5px] tw-text-[color:var(--st-text-faint)]">
                    {storeConnected ? '실시간 연결됨' : '연결 대기중'}
                </span>
            </div>

            {/*메세지*/}
            <div ref={scrollRef} className="st-scroll tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-5">
                {messages.length === 0 ? (
                    <Empty title="아직 주고받은 대화가 없습니다">
                        연동이나 정산, 무엇이든 편하게 남겨주세요. 담당자가 확인하는 대로 답변드립니다.
                    </Empty>
                ) : messages.map((msg) => {
                    const mine = String(msg.chatRole) === MY_ROLE;
                    return (
                        <div key={msg.seqNum}
                             className={`tw-mb-3.5 tw-flex ${mine ? 'tw-justify-end' : 'tw-justify-start'}`}>
                            <div className={`tw-flex tw-max-w-[80%] tw-items-end tw-gap-1.5 ${mine ? 'tw-flex-row-reverse' : 'tw-flex-row'}`}>

                                {/*말풍선*/}
                                <div className={`tw-break-words tw-text-[13.5px] tw-leading-[1.7] tw-whitespace-pre-wrap ${
                                    isImageMessage(msg) || isVideoMessage(msg) ? 'tw-p-1.5' : 'tw-px-4 tw-py-2.5'}`}
                                     style={mine ? {
                                         background: 'linear-gradient(135deg, #FFD79A, #FFBE48)',
                                         color: '#4A3410',
                                         borderRadius: '16px 16px 5px 16px',
                                         boxShadow: '0 1px 0 rgba(255,255,255,.5) inset, 0 6px 16px -10px rgba(240,163,31,.9)',
                                     } : {
                                         background: 'var(--st-field)',
                                         color: 'var(--st-text)',
                                         border: '1px solid var(--st-glass-line)',
                                         borderRadius: '16px 16px 16px 5px',
                                         boxShadow: '0 4px 14px -10px rgba(43,36,64,.4)',
                                     }}>

                                    {isImageMessage(msg) ? (
                                        <div>
                                            <img src={msg.previewUrl} alt={msg.fileNm}
                                                 onError={(e) => onImgError(e, msg)}
                                                 className="tw-block tw-w-full tw-max-w-[260px] tw-rounded-[12px] tw-object-contain"/>
                                            <a href={downloadUrl(msg)}
                                               className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-px-1 tw-text-[11.5px] tw-underline tw-underline-offset-2"
                                               style={{color: mine ? '#6B4A14' : 'var(--st-text-sub)'}}>
                                                <TbDownload/> 원본 받기
                                            </a>
                                        </div>
                                    ) : isVideoMessage(msg) ? (
                                        <div>
                                            <video src={msg.fileUrl} controls preload="metadata"
                                                   className="tw-block tw-w-full tw-max-w-[280px] tw-rounded-[12px]"/>
                                            <a href={downloadUrl(msg)}
                                               className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-px-1 tw-text-[11.5px] tw-underline tw-underline-offset-2"
                                               style={{color: mine ? '#6B4A14' : 'var(--st-text-sub)'}}>
                                                <TbDownload/> 원본 받기
                                            </a>
                                        </div>
                                    ) : isFileMessage(msg) ? (
                                        <a href={downloadUrl(msg)}
                                           className="tw-inline-flex tw-items-center tw-gap-2 tw-underline tw-underline-offset-2"
                                           style={{color: mine ? '#4A3410' : 'var(--st-text)'}}>
                                            <TbPaperclip/> {msg.fileNm || msg.chatKey}
                                        </a>
                                    ) : msg.chatKey}
                                </div>

                                <span className="tw-flex tw-flex-shrink-0 tw-flex-col tw-items-end tw-gap-0.5 tw-pb-0.5">
                                    {/* 상대가 아직 읽지 않은 내 메세지에만 표시된다.
                                        상대가 읽으면 서버가 읽음신호를 보내 이 표시가 즉시 사라진다. */}
                                    {mine && Number(msg.unreadCount) > 0 ? (
                                        <span className="tw-rounded-full tw-px-1.5 tw-text-[10px] tw-font-bold tw-leading-[15px]"
                                              style={{background: 'rgba(255,190,72,.22)', color: '#B8790F'}}>
                                            안읽음
                                        </span>
                                    ) : null}
                                    <span className="st-num tw-text-[10.5px] tw-text-[color:var(--st-text-faint)]">
                                        {timeText(msg)}
                                    </span>
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/*입력영역*/}
            <div className="tw-relative tw-flex-shrink-0 tw-border-t tw-px-4 tw-py-3.5"
                 style={{borderColor: 'var(--st-glass-line)'}}>

                {/*
                    이모티콘 선택기는 '띄우는' 요소다.
                    문서흐름에 두면 입력줄과 도구버튼이 아래로 밀려 컨테이너 밖으로 잘려나간다.
                    절대배치로 입력영역 위에 겹쳐 띄워 버튼 위치를 고정한다.
                */}
                {emojiOpen ? (
                    <div className="tw-absolute tw-bottom-full tw-left-3 tw-z-30 tw-mb-2">
                        <Picker onEmojiSelect={pickEmoji}
                                previewPosition="none" searchPosition="none" skinTonePosition="none" locale="kr"/>
                    </div>
                ) : null}

                {uploadError ? (
                    <p className="tw-mb-2 tw-text-[12px] tw-text-[#E0654C]">{uploadError}</p>
                ) : null}

                <div className="tw-flex tw-items-end tw-gap-2.5">
                    <textarea ref={textareaRef} className="st-textarea st-scroll tw-flex-1" value={text} rows={1}
                              placeholder="메세지를 입력해주세요"
                              onChange={(e) => handleTextChange(e.target.value)}
                              onKeyDown={onKeyDown}
                              style={{minHeight: '46px', maxHeight: '140px'}}/>
                    <button type="button" onClick={handleSend} disabled={sending || !text.trim()}
                            aria-label="메세지 보내기"
                            className="st-btn st-btn-dawn st-btn-md st-btn-icon tw-flex-shrink-0 tw-text-[18px]">
                        {sending ? <span className="st-spin st-spin-ink"/> : <TbSend/>}
                    </button>
                </div>

                <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2.5">
                    <button type="button" data-store-emoji-toggle="1" onClick={() => setEmojiOpen(v => !v)}
                            className="st-btn st-btn-glass st-btn-sm">
                        <TbMoodSmile/> 이모티콘
                    </button>
                    <button type="button" onClick={handleFilePick} disabled={uploading}
                            className="st-btn st-btn-glass st-btn-sm">
                        {uploading ? <span className="st-spin tw-h-3.5 tw-w-3.5"/> : <TbPaperclip/>}
                        {uploading ? '보내는 중' : '파일첨부'}
                    </button>
                    <input ref={fileInputRef} type="file" onChange={handleFileChange} className="tw-hidden"/>
                </div>
            </div>
        </div>
    );
}

export default StoreChatPanel;
